// Cálculo de los datos para el pop-up de detalle por empleado.
//   #2 Horas por día (últimos 30 días con datos), split facturable / no-facturable
//   #3 Tendencia semanal del % de cumplimiento facturable (todo el histórico del seed)
//
// Reutiliza los helpers de días hábiles del reportEngine y la meta por jornada.
import {
  countWeekdays,
  countWeekdaysInRange,
  type ReportData,
} from './reportEngine';
import { JORNADA_HORAS, type SeedTimeEntry } from '../data/types';
import { HOLIDAY_BY_DATE } from '../data/holidays-cl';

export interface DayHours {
  date: string; // YYYY-MM-DD
  billable: number;
  nonBillable: number;
}

export interface WeekPoint {
  weekStart: string; // lunes, YYYY-MM-DD
  label: string; // ej "12 may"
  pctBillable: number; // cargado / exigible
  expected: number;
  actual: number;
}

// Estado de un día para la "Disponibilidad semanal".
export type AvailabilityStatus =
  | 'disponible'
  | 'vacaciones'
  | 'licencia'
  | 'administrativo'
  | 'feriado'
  | 'actividad_oficina';

export interface DayAvailability {
  date: string; // YYYY-MM-DD
  weekdayLabel: string; // 'Lun', 'Mar', ...
  status: AvailabilityStatus;
  media: boolean; // true si es media jornada (adm 0.5 o actividad media)
  detail?: string; // nombre del feriado / actividad, si aplica
}

export interface EmployeeDetail {
  dailyHours: DayHours[]; // #2
  weeklyTrend: WeekPoint[]; // #3
  periodEntries: SeedTimeEntry[]; // registros del período seleccionado (tabla detalle)
  weekAvailability: DayAvailability[]; // disponibilidad lun-vie (solo si período = 1 semana)
  hasData: boolean;
}

function addDays(date: string, days: number): string {
  const d = new Date(date + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Lunes de la semana de una fecha dada (ISO: semana empieza lunes).
function mondayOf(date: string): string {
  const d = new Date(date + 'T00:00:00');
  const dow = d.getDay(); // 0=dom..6=sáb
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function shortLabel(date: string): string {
  const [, m, d] = date.split('-');
  return `${parseInt(d, 10)} ${MONTHS_ES[parseInt(m, 10) - 1]}`;
}

export function computeEmployeeDetail(
  employeeId: string,
  data: ReportData,
  period?: { startDate: string; endDate: string },
  // Si se pasa, el gráfico "Horas por día" (#2) usa EXACTAMENTE este rango en
  // vez de la ventana fija de últimos 30 días con datos. Útil para acotar el
  // gráfico al período (semana/mes) que el usuario eligió arriba.
  dailyHoursRange?: { startDate: string; endDate: string }
): EmployeeDetail {
  const entries = data.timeEntries.filter((e) => e.employeeId === employeeId);
  const absences = data.absences.filter((a) => a.employeeId === employeeId);
  const adminDays = data.adminDays.filter((d) => d.employeeId === employeeId);

  // Horas meta por día hábil según la jornada del colaborador (Full 7 / Part 4).
  const emp = data.employees.find((e) => e.id === employeeId);
  const horasJornada = JORNADA_HORAS[emp?.jornada ?? 'full_time'];

  // Registros del período seleccionado, ordenados por fecha (para la tabla detalle).
  const periodEntries = period
    ? entries
        .filter((e) => e.date >= period.startDate && e.date <= period.endDate)
        .sort((a, b) => a.date.localeCompare(b.date))
    : [];

  // Disponibilidad semanal: solo si el período es exactamente una semana (7 días).
  const weekAvailability = computeWeekAvailability(
    period,
    absences,
    adminDays,
    data.nonWorkingDays ?? []
  );

  if (entries.length === 0) {
    return {
      dailyHours: [],
      weeklyTrend: [],
      periodEntries,
      weekAvailability,
      hasData: false,
    };
  }

  const dates = entries.map((e) => e.date).sort();
  const minDate = dates[0];
  const maxDate = dates[dates.length - 1];

  // ── #2: horas por día. Si se pasó un rango explícito (semana/mes elegido),
  // se usa ESE rango tal cual; si no, ventana fija de últimos 30 días con datos. ──
  const windowStart = dailyHoursRange ? dailyHoursRange.startDate : addDays(maxDate, -29);
  const windowEnd = dailyHoursRange ? dailyHoursRange.endDate : maxDate;
  const byDay = new Map<string, DayHours>();
  for (let day = windowStart; day <= windowEnd; day = addDays(day, 1)) {
    byDay.set(day, { date: day, billable: 0, nonBillable: 0 });
  }
  for (const e of entries) {
    const slot = byDay.get(e.date);
    if (!slot) continue;
    if (e.billable) slot.billable += e.durationHours;
    else slot.nonBillable += e.durationHours;
  }
  const dailyHours = Array.from(byDay.values());

  // ── #3: tendencia semanal del % facturable, desde la 1ª hasta la última semana con datos ──
  // Primero armo la lista de lunes (uno por semana) y luego calculo cada uno.
  const weekStarts: string[] = [];
  const lastWeekStart = mondayOf(maxDate);
  for (let ws = mondayOf(minDate); ws <= lastWeekStart; ws = addDays(ws, 7)) {
    weekStarts.push(ws);
  }

  const weeklyTrend: WeekPoint[] = weekStarts.map((ws) => {
    const we = addDays(ws, 6);

    // horas facturables cargadas esa semana
    const actual = entries
      .filter((e) => e.billable && e.date >= ws && e.date <= we)
      .reduce((s, e) => s + e.durationHours, 0);

    // exigible = (días hábiles − ausencias) × 6
    const workingDays = countWeekdays(
      new Date(ws + 'T00:00:00'),
      new Date(we + 'T00:00:00')
    );
    const absenceDays = absences.reduce(
      (s, a) => s + countWeekdaysInRange(a.startDate, a.endDate, ws, we),
      0
    );
    const adminDayCount = adminDays
      .filter((d) => d.date >= ws && d.date <= we)
      .reduce((s, d) => s + d.quantity, 0);
    // Feriados + actividades oficina de la semana (solo días hábiles).
    const nonWorking = (data.nonWorkingDays ?? []).reduce((s, nw) => {
      if (nw.date < ws || nw.date > we) return s;
      const dow = new Date(nw.date + 'T00:00:00').getDay();
      if (dow === 0 || dow === 6) return s;
      return s + (nw.portion === 'media_jornada' ? 0.5 : 1);
    }, 0);
    const workableDays = Math.max(
      0,
      workingDays - absenceDays - adminDayCount - nonWorking
    );
    const expected = workableDays * horasJornada;

    return {
      weekStart: ws,
      label: shortLabel(ws),
      pctBillable: expected > 0 ? actual / expected : 0,
      expected,
      actual,
    };
  });

  return {
    dailyHours,
    weeklyTrend,
    periodEntries,
    weekAvailability,
    hasData: true,
  };
}

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];

// Estado de cada día lun-vie de la semana del período. Vacío si el período no
// es exactamente una semana (7 días). La prioridad de estados sigue la misma
// lógica del motor: la ausencia/feriado/actividad que cubra el día lo marca.
function computeWeekAvailability(
  period: { startDate: string; endDate: string } | undefined,
  absences: { type: string; startDate: string; endDate: string }[],
  adminDays: { date: string; quantity: number }[],
  nonWorkingDays: { date: string; portion: string }[]
): DayAvailability[] {
  if (!period) return [];
  // ¿el período abarca exactamente 7 días? (semana)
  const spanDays =
    (new Date(period.endDate + 'T00:00:00').getTime() -
      new Date(period.startDate + 'T00:00:00').getTime()) /
      86400000 +
    1;
  if (Math.round(spanDays) !== 7) return [];

  const monday = period.startDate;
  const result: DayAvailability[] = [];

  for (let i = 0; i < 5; i++) {
    const date = addDays(monday, i);

    // Ausencia personal (vacación / licencia)
    const abs = absences.find((a) => date >= a.startDate && date <= a.endDate);
    // Día administrativo (puede ser 0.5)
    const adm = adminDays.find((d) => d.date === date);
    // Feriado / actividad oficina de empresa
    const nw = nonWorkingDays.find((n) => n.date === date);

    let status: AvailabilityStatus = 'disponible';
    let media = false;
    let detail: string | undefined;

    const holiday = HOLIDAY_BY_DATE[date];
    if (holiday) {
      status = 'feriado';
      detail = holiday.name;
    } else if (nw) {
      status = 'actividad_oficina';
      media = nw.portion === 'media_jornada';
    } else if (abs) {
      status = abs.type === 'medical' ? 'licencia' : 'vacaciones';
    } else if (adm) {
      status = 'administrativo';
      media = adm.quantity <= 0.5;
    }

    result.push({ date, weekdayLabel: WEEKDAY_LABELS[i], status, media, detail });
  }

  return result;
}
